'use client'

import { IconEmailL } from 'icons/components/IconEmailL'
import { IconHomeL } from 'icons/components/IconHomeL'
import { IconLocationL } from 'icons/components/IconLocationL'
import { IconStoreL } from 'icons/components/IconStoreL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useMemo } from 'react'
import { RETURN_ORDER_TYPES } from 'shipping-and-returns/constants/returnOrderTypes'
import { RETURN_TYPES } from 'shipping-and-returns/constants/returnTypes'
import { ByPostContent } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/ByPostContent/ByPostContent'
import { ByPostTitle } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/ByPostContent/ByPostTitle'
import { DropPointContent } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/DropPointContent/DropPointContent'
import { DropPointTitle } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/DropPointContent/DropPointTitle'
import { HomeDeliveryContent } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/HomeDeliveryContent/HomeDeliveryContent'
import { HomeDeliveryTitle } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/HomeDeliveryContent/HomeDeliveryTitle'
import { StoreContent } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/StoreContent/StoreContent'
import { StoreTitle } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/StoreContent/StoreTitle'
import { useBlocksByReturnsLimit } from 'shipping-and-returns/hooks/useBlocksByReturnsLimit/useBlocksByReturnsLimit'
import { useReturnsInfo } from 'shipping-and-returns/hooks/useReturnsInfo/useReturnsInfo'
import type { UnavailableReturnMethod } from 'shipping-and-returns/types/ReturnsInfo.types'
import { getMethodInfoByReturnType } from 'shipping-and-returns/utils/getMethodInfoByReturnType/getMethodInfoByReturnType'
import { hasReturnMethod } from 'shipping-and-returns/utils/hasReturnMethod/hasReturnMethod'
import { Channel } from 'types/masterData'

interface UseReturnMethodsInfoProps {
	orderType?: string
	orderId?: string
	purchaseId?: string
	unavailableReturnMethods?: UnavailableReturnMethod[]
	barcode?: string | null
	onCloseEvent?: () => void
	onClickAccordion?: (methodId: string) => void
	isMyPurchasesPage?: boolean
	onLocateStoreClick?: () => void
}
export const useReturnMethodsInfo = ({
	orderType,
	orderId,
	purchaseId,
	unavailableReturnMethods,
	barcode,
	onCloseEvent = () => {},
	onClickAccordion = () => {},
	isMyPurchasesPage,
	onLocateStoreClick,
}: UseReturnMethodsInfoProps) => {
	const { t } = useLabels()
	const { channel } = useMasterData()

	const {
		isOrderBlockedByReturnsLimit,
		homePickUpBlockedMessage,
		dropPointBlockedMessage,
	} = useBlocksByReturnsLimit(unavailableReturnMethods)

	const isOutlet = channel === Channel.Outlet
	const {
		maximumReturnDays,
		returnMethods,
		hasReturnCostSinceDate,
		hasSecondReturnsBlock,
		isDroppointMethodTempDisabled,
	} = useReturnsInfo()

	// Logic needed to add drop point method when it is disabled temporarily
	const returnMethodsList = useMemo(
		() => [
			...returnMethods,
			...(isDroppointMethodTempDisabled &&
			!hasReturnMethod(returnMethods, [RETURN_TYPES.DROPPOINT])
				? [
						{
							id: RETURN_TYPES.DROPPOINT,
							isFree: true,
							cost: { price: '-', additionalPrices: [] },
						},
					]
				: []),
		],
		[returnMethods, isDroppointMethodTempDisabled]
	)

	const methodsInfo = useMemo(() => {
		const [
			inStoreReturnInfo,
			homePickUpInfo,
			dropPointReturnInfo,
			byPostReturnInfo,
		] = getMethodInfoByReturnType(returnMethodsList, [
			[RETURN_TYPES.SHOP],
			[RETURN_TYPES.HOME_PICK_UP, RETURN_TYPES.HOME_PICK_UP_WITHOUT_REQUEST],
			[
				RETURN_TYPES.DROPPOINT,
				RETURN_TYPES.COLLECTION_POINT_WITHOUT_REQUEST,
				RETURN_TYPES.PREPAID_RETURN_LABEL,
			],
			[RETURN_TYPES.MAIL],
		])

		const inStoreConfig = {
			id: RETURN_TYPES.SHOP,
			icon: <IconStoreL />,
			'data-testid': `myPurchases.returns.accordion.store`,
			isFree: !!inStoreReturnInfo?.isFree,
			cost: inStoreReturnInfo?.cost?.price ?? '',
			hasEcoContent: true,
			shouldScroll: false,
			title: (
				<StoreTitle
					isFree={!!inStoreReturnInfo?.isFree}
					price={inStoreReturnInfo?.cost?.price ?? ''}
					orderType={orderType}
				/>
			),
			onClick: (_expanded: boolean) => onClickAccordion('store'),
			content: (
				<StoreContent
					orderId={orderId}
					barcode={barcode}
					isMyPurchasesPage={isMyPurchasesPage}
					onCloseEvent={onCloseEvent}
					onLocateStoreClick={onLocateStoreClick}
				/>
			),
		}

		if (orderType === RETURN_ORDER_TYPES.STORE) {
			return [inStoreConfig]
		}

		const isDropPointWithoutRequest = hasReturnMethod(returnMethodsList, [
			RETURN_TYPES.COLLECTION_POINT_WITHOUT_REQUEST,
			RETURN_TYPES.PREPAID_RETURN_LABEL,
		])

		const isHomePickUpWithRequest = hasReturnMethod(
			returnMethodsList,
			RETURN_TYPES.HOME_PICK_UP
		)

		return [
			...(!!inStoreReturnInfo ? [inStoreConfig] : []),
			...(!!dropPointReturnInfo
				? [
						{
							id: RETURN_TYPES.DROPPOINT,
							icon: <IconLocationL />,
							'data-testid': `myPurchases.returns.accordion.dropPoint`,
							isFree: !!dropPointReturnInfo.isFree,
							cost: dropPointReturnInfo.cost.price,
							shouldScroll: false,
							title: (
								<DropPointTitle
									isFree={dropPointReturnInfo.isFree}
									price={dropPointReturnInfo.cost.price}
									dropPointBlockedMessage={dropPointBlockedMessage}
									isDroppointMethodTempDisabled={isDroppointMethodTempDisabled}
								/>
							),
							onClick: (_expanded: boolean) => onClickAccordion('dropPoint'),
							content: (
								<DropPointContent
									isFindDropointLink={isDropPointWithoutRequest}
									isBlocked={isOrderBlockedByReturnsLimit}
									hasSecondReturnsBlock={hasSecondReturnsBlock}
									hasHomePickUpRequest={isHomePickUpWithRequest}
									orderId={orderId}
									purchaseId={purchaseId}
									isDroppointMethodTempDisabled={isDroppointMethodTempDisabled}
								/>
							),
						},
					]
				: []),
			...(!!homePickUpInfo
				? [
						{
							id: RETURN_TYPES.HOME_PICK_UP,
							icon: <IconHomeL />,
							'data-testid': `myPurchases.returns.accordion.homePickup`,
							isFree: !!homePickUpInfo.isFree,
							cost: homePickUpInfo.cost.price,
							shouldScroll: false,
							title: (
								<HomeDeliveryTitle
									isFree={homePickUpInfo.isFree}
									price={homePickUpInfo.cost.price}
									homePickUpBlockedMessage={homePickUpBlockedMessage}
								/>
							),
							onClick: (_expanded: boolean) => onClickAccordion('home'),
							content: (
								<HomeDeliveryContent
									returnCost={homePickUpInfo.cost.price ?? ''}
									isFreePickUp={!!homePickUpInfo.isFree}
									hasReturnCostSinceDate={hasReturnCostSinceDate}
									orderId={orderId}
									purchaseId={purchaseId}
									isBlocked={isOrderBlockedByReturnsLimit}
									hasDropPointRequest={
										!!dropPointReturnInfo && !isDropPointWithoutRequest
									}
									isRequestNeeded={isHomePickUpWithRequest}
									hasSecondReturnsBlock={hasSecondReturnsBlock}
								/>
							),
						},
					]
				: []),
			...(byPostReturnInfo
				? [
						{
							id: RETURN_TYPES.MAIL,
							icon: <IconEmailL />,
							'data-testid': `myPurchases.returns.accordion.byPost`,
							cost: t('myPurchases.online.details.returns.hasCost.text'),
							isFree: false,
							shouldScroll: false,
							title: <ByPostTitle />,
							onClick: (_expanded: boolean) => onClickAccordion('email'),
							content: <ByPostContent isOutlet={isOutlet} />,
						},
					]
				: []),
		]
	}, [returnMethodsList, orderType])

	return {
		returnMethods: returnMethodsList,
		maximumReturnDays,
		methodsInfo,
	}
}
